/**
 * PermissionImpactModal (S3.3) — the change-impact DIFF shown before a matrix
 * save commits (plan §Slice 3.3; fleet `PermissionImpactAnalysis` /
 * `PermissionDiffPanel` / `AffectedUserPreview` — replicated on the REAL
 * resolver, not fleet's direct-grant diff).
 *
 * The diff is computed over EFFECTIVE grants: every non-system role is probed
 * through the shared pure `resolveEffectiveAccess` (Axis A only — the WHAT
 * matrix never reads WHERE) under the ORIGINAL catalog and again under the
 * DRAFT-overlaid catalog. That way the modal is honest about the full blast
 * radius of a save:
 *   • toggling a direct grant on role-finance shows on role-finance;
 *   • editing a junior role (or an `inheritsFrom` edge) ALSO surfaces the
 *     senior roles whose inherited grants change — fleet's diff could not see
 *     this because fleet never had a live resolver (plan §0 thesis);
 *   • implication-expanded grants (`products.manage ⇒ products.view`) ride in
 *     automatically.
 *
 * Affected members = users holding ANY impacted role — the people whose
 * stamped snapshot will be reprojected by the save.
 *
 * DELTA COLOUR LAW (plan §Slice 3.3, retained from the merge resolutions):
 * added = warm-blue `processing`, removed = NEUTRAL slate `default` — never
 * green/red. A grant removal is a settled administrative change, not an error,
 * so the confirm button is a plain primary (no danger styling). Each delta chip
 * carries a +/− glyph AND a full-word tooltip AND an aria-label, so the
 * direction is never colour-only (ux-lens colour-only rule).
 *
 * Flow (RolesTab save path, rbacV2 ON only): Save → THIS modal → Continue →
 * the existing `ReasonRequiredModal` gate → audit row (unchanged). Flag OFF
 * keeps today's Save → reason flow byte-identical.
 *
 * Permissions render by their HUMAN `governance.permissions.*` label via the
 * shared `permissionLabelKey` — the raw dotted code never renders.
 */

import { useMemo } from 'react';
import { Modal, Space, Tag, Tooltip, Typography, theme } from 'antd';
import { MinusOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { EXTENDED_PERMISSIONS, PERMISSION_CATALOG } from '@/constants/routePermissions';
import { resolveEffectiveAccess } from '@/lib/rbac';
import { controlHeightTokens } from '@/styles/tokens';
import { permissionLabelKey, type MatrixRoleInput } from './PermissionMatrixGrid';
import type { GovernanceUser } from './model';

const { Text } = Typography;

/* eslint-disable react-refresh/only-export-components -- the pure impact-model
   builder is exported for node-env tests (house precedent:
   PermissionMatrixGrid / EffectivePermissionPanel pure exports). */

/** Catalog render order for diff chips (unknown codes append alphabetically). */
const CODE_ORDER: ReadonlyMap<string, number> = new Map(
  [...PERMISSION_CATALOG.flatMap((module) => module.permissions), ...EXTENDED_PERMISSIONS].map(
    (code, index) => [code, index] as const
  )
);

function byCatalogOrder(a: string, b: string): number {
  return (
    (CODE_ORDER.get(a) ?? Number.MAX_SAFE_INTEGER) -
      (CODE_ORDER.get(b) ?? Number.MAX_SAFE_INTEGER) || a.localeCompare(b)
  );
}

/** One impacted role — its effective added/removed codes (catalog order). */
export interface RoleImpactModel {
  roleId: string;
  roleName: string;
  added: readonly string[];
  removed: readonly string[];
}

export interface AffectedMemberModel {
  id: string;
  name: string;
}

export interface PermissionImpactModel {
  /** Only roles whose EFFECTIVE grant set actually changes (draft vs server). */
  roles: readonly RoleImpactModel[];
  /** Users holding any impacted role — their snapshot reprojects on save. */
  affectedMembers: readonly AffectedMemberModel[];
}

const EMPTY_IMPACT: PermissionImpactModel = { roles: [], affectedMembers: [] };

/**
 * Probe every non-system role AS a principal holding just itself (the S2.2
 * matrix probe discipline — Axis A only, `orgs` empty) and return
 * roleId → effective grant set. System roles are skipped: the admin column is
 * locked in the matrix and can never diff.
 */
function probeEffectiveGrants(
  roles: readonly MatrixRoleInput[]
): Map<string, ReadonlySet<string>> {
  const out = new Map<string, ReadonlySet<string>>();
  for (const role of roles) {
    if (role.isSystem) continue;
    out.set(
      role.id,
      resolveEffectiveAccess({
        identity: { userId: `impact-probe-${role.id}`, roleIds: [role.id], assignments: [] },
        roles,
        orgs: [],
      }).grants
    );
  }
  return out;
}

/**
 * The pure impact selector — zero React, node-testable, same discipline as the
 * resolver it consumes. Diffs each role's effective grants between the server
 * catalog and the draft-overlaid catalog, then collects the members of every
 * impacted role.
 */
export function buildPermissionImpactModel(
  originalRoles: readonly MatrixRoleInput[],
  draftRoles: readonly MatrixRoleInput[],
  users: readonly GovernanceUser[]
): PermissionImpactModel {
  const before = probeEffectiveGrants(originalRoles);
  const after = probeEffectiveGrants(draftRoles);

  const roles: RoleImpactModel[] = [];
  for (const draft of draftRoles) {
    if (draft.isSystem) continue;
    const beforeGrants = before.get(draft.id) ?? new Set<string>();
    const afterGrants = after.get(draft.id) ?? new Set<string>();
    const added = [...afterGrants].filter((code) => !beforeGrants.has(code)).sort(byCatalogOrder);
    const removed = [...beforeGrants].filter((code) => !afterGrants.has(code)).sort(byCatalogOrder);
    if (added.length > 0 || removed.length > 0) {
      roles.push({ roleId: draft.id, roleName: draft.name, added, removed });
    }
  }

  const impactedIds = new Set(roles.map((role) => role.roleId));
  const affectedMembers = users
    .filter((user) => user.roleIds.some((roleId) => impactedIds.has(roleId)))
    .map((user) => ({ id: user.id, name: user.name }));

  return { roles, affectedMembers };
}

/* eslint-enable react-refresh/only-export-components */

export interface PermissionImpactModalProps {
  open: boolean;
  /** The server roles (pre-edit baseline). `GovernanceRole` is assignable. */
  originalRoles: readonly MatrixRoleInput[];
  /** The draft-overlaid roles (RolesTab edit buffers via `overlayDraft`). */
  draftRoles: readonly MatrixRoleInput[];
  users: readonly GovernanceUser[];
  /** Proceed to the existing reason gate — the save itself happens there. */
  onConfirm: () => void;
  onCancel: () => void;
}

const PermissionImpactModal = ({
  open,
  originalRoles,
  draftRoles,
  users,
  onConfirm,
  onCancel,
}: PermissionImpactModalProps) => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();

  const model = useMemo(
    () => (open ? buildPermissionImpactModel(originalRoles, draftRoles, users) : EMPTY_IMPACT),
    [open, originalRoles, draftRoles, users]
  );

  /** Human `governance.permissions.*` label — the ONLY way a code renders. */
  const permissionLabel = (code: string): string =>
    t(`permissions.${permissionLabelKey(code)}`);

  /**
   * One delta chip. Colour law (file docblock): added = warm-blue `processing`,
   * removed = neutral slate `default` — NOT green/red. Direction is carried by
   * the +/− glyph + full-word tooltip + aria-label, never colour alone.
   */
  const deltaChip = (code: string, kind: 'added' | 'removed') => {
    const label = permissionLabel(code);
    const word = t(kind === 'added' ? 'impact.addedTooltip' : 'impact.removedTooltip');
    return (
      <Tooltip key={`${kind}-${code}`} title={word}>
        <Tag
          color={kind === 'added' ? 'processing' : 'default'}
          icon={kind === 'added' ? <PlusOutlined /> : <MinusOutlined />}
          aria-label={`${word}: ${label}`}
          style={{ marginInlineEnd: 0 }}
        >
          {label}
        </Tag>
      </Tooltip>
    );
  };

  return (
    <Modal
      open={open}
      title={t('impact.title')}
      okText={t('impact.confirm')}
      cancelText={t('impact.cancel')}
      onOk={onConfirm}
      onCancel={onCancel}
      width={600}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginSM }}>
        {t('impact.hint')}
      </Text>

      {model.roles.length === 0 ? (
        // Reachable when only role DEFINITION changed (e.g. removing a
        // redundant inherits edge) — the save is still meaningful, so the
        // modal says so honestly and lets the operator continue.
        <Text style={{ display: 'block' }}>{t('impact.noChanges')}</Text>
      ) : (
        <>
          {model.roles.map((role) => (
            <div key={role.roleId} style={{ marginBottom: token.marginMD }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: token.marginXS,
                  marginBottom: token.marginXXS,
                }}
              >
                <Text strong>{role.roleName}</Text>
                {role.added.length > 0 && (
                  <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                    {t('impact.addedCount', { count: role.added.length })}
                  </Tag>
                )}
                {role.removed.length > 0 && (
                  <Tag style={{ marginInlineEnd: 0 }}>
                    {t('impact.removedCount', { count: role.removed.length })}
                  </Tag>
                )}
              </div>
              {role.added.length > 0 && (
                <Space wrap size={4} style={{ marginBottom: token.marginXXS }}>
                  {role.added.map((code) => deltaChip(code, 'added'))}
                </Space>
              )}
              {role.removed.length > 0 && (
                <Space wrap size={4}>
                  {role.removed.map((code) => deltaChip(code, 'removed'))}
                </Space>
              )}
            </div>
          ))}

          {/* Affected members — whose stamped snapshot reprojects on save. */}
          <div style={{ marginTop: token.marginLG }}>
            <Space size={6} style={{ marginBottom: token.marginXXS }}>
              <TeamOutlined aria-hidden style={{ color: token.colorTextSecondary }} />
              <Text strong>
                {t('impact.affectedMembers', { count: model.affectedMembers.length })}
              </Text>
            </Space>
            {model.affectedMembers.length > 0 ? (
              <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                <Space wrap size={4}>
                  {model.affectedMembers.map((member) => (
                    <Tag key={member.id} style={{ marginInlineEnd: 0 }}>
                      {member.name}
                    </Tag>
                  ))}
                </Space>
              </div>
            ) : (
              <Text type="secondary" style={{ display: 'block' }}>
                {t('impact.noAffectedMembers')}
              </Text>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default PermissionImpactModal;
